import { ArrowLeft, KeyRound, Lock, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation } from "react-router";

import { ApiError } from "@/api/client";
import { useMeta } from "@/api/meta";
import { useAuth } from "@/app/auth";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";

import { AuthShell } from "./AuthShell";

const knownErrors = [
  "invalid_credentials",
  "too_many_attempts",
  "invalid_mfa_code",
  "session_expired",
] as const;
type KnownError = (typeof knownErrors)[number];

function toKnown(err: unknown): KnownError | "generic" {
  const code = err instanceof ApiError ? err.code : "";
  return (knownErrors as readonly string[]).includes(code) ? (code as KnownError) : "generic";
}

export function LoginPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const meta = useMeta();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<KnownError | "generic" | null>(null);
  const [pending, setPending] = useState(false);

  if (auth.status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }
  if (meta.data?.setup_required) return <Navigate to="/setup" replace />;

  const run = async (fn: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      const known = toKnown(err);
      setError(known);
      if (known === "session_expired") {
        setMfaToken(null);
        setCode("");
      }
    } finally {
      setPending(false);
    }
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await auth.login(email.trim(), password);
      if (res) {
        setMfaToken(res.mfaToken);
        setPassword("");
      }
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    void run(() => auth.verifyMfa(mfaToken, code.replace(/\s/g, "")));
  };

  const errorAlert = error && <Alert variant="danger">{t(`login.errors.${error}`)}</Alert>;

  if (mfaToken) {
    return (
      <AuthShell title={t("login.mfaTitle")} subtitle={t("login.mfaSubtitle")}>
        <form className="flex flex-col gap-5" onSubmit={submitCode} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mfa-code">{t("login.mfaCode")}</Label>
            <div className="relative">
              <KeyRound
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={7}
                className="h-11 pl-9 font-mono text-lg tracking-[0.3em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
                aria-invalid={error === "invalid_mfa_code" || undefined}
              />
            </div>
          </div>
          {errorAlert}
          <Button type="submit" size="lg" disabled={pending || code.replace(/\s/g, "").length !== 6}>
            {t("login.mfaSubmit")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setMfaToken(null);
              setCode("");
              setError(null);
            }}
          >
            <ArrowLeft /> {t("login.mfaBack")}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("login.title")} subtitle={t("login.subtitle")} footer={t("login.noAccount")}>
      <form className="flex flex-col gap-5" onSubmit={submitPassword} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t("login.email")}</Label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              className="h-11 pl-9"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error === "invalid_credentials" || undefined}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              {t("login.forgot")}
            </Link>
          </div>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              className="h-11 pl-9"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error === "invalid_credentials" || undefined}
            />
          </div>
        </div>
        {errorAlert}
        <Button type="submit" size="lg" disabled={pending || !email || !password}>
          {pending ? t("login.submitting") : t("login.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
